import * as React from 'react';
import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import type { CustomerStatus, CustomerType, SpaceWithSites } from '@klareospace/pharos-lib-types';
import { CUSTOMER_STATUS, CUSTOMER_TYPE } from '@klareospace/pharos-lib-types';
import {
  ActionBar,
  ActionBarDivider,
  ActionBarSpacer,
  Button,
  GroupedMultiSelect,
  MultiSelect,
  SearchInput,
} from '@klareospace/pharos-lib-web-utils';
import type { GroupedMultiSelectGroup, MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import { useMasterContext } from '&/master';
import { useSpacesWithSites } from '../../settings/spaces/hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomersActionBarProps {
  onCreateClick?: () => void;
  onScopeFilterChange?: (values: string[]) => void;
  onSearchChange?: (value: string) => void;
  onStatusFilterChange?: (values: string[]) => void;
  onTypeFilterChange?: (values: string[]) => void;
  scopeFilter?: string[];
  searchValue?: string;
  statusFilter?: CustomerStatus[];
  typeFilter?: CustomerType[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MULTI_SELECT_COMMON_PROPS = {
  hideClear: true,
  hideClose: true,
  maxCount: 2,
  showLabelWhenFiltering: true,
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

export function CustomersActionBar({
  onCreateClick,
  onScopeFilterChange,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  scopeFilter = [],
  searchValue = '',
  statusFilter = [],
  typeFilter = [],
}: CustomersActionBarProps): React.JSX.Element {
  const { t } = useTranslation('customers');
  const { siteId, spaceId } = useMasterContext();

  const { data: spacesWithSites } = useSpacesWithSites({ enabled: !siteId });

  const spaceSiteGroups: GroupedMultiSelectGroup[] = useMemo(
    () =>
      (spacesWithSites ?? []).map((space: SpaceWithSites) => ({
        children: space.sites.map((site) => ({ label: site.name, value: site.id })),
        label: space.name,
        value: space.id,
      })),
    [spacesWithSites],
  );

  const siteOptions: MultiSelectOption[] = useMemo(() => {
    if (!spaceId) {
      return [];
    }
    const space = (spacesWithSites ?? []).find((s: SpaceWithSites) => s.id === spaceId);
    return (space?.sites ?? []).map((site) => ({ label: site.name, value: site.id }));
  }, [spacesWithSites, spaceId]);

  const statusOptions = useMemo<MultiSelectOption[]>(
    () => [{ label: t('filters.statuses.validated'), value: CUSTOMER_STATUS.VALIDATED }],
    [t],
  );

  const typeOptions = useMemo<MultiSelectOption[]>(
    () => [
      { label: t('filters.types.client'), value: CUSTOMER_TYPE.CLIENT },
      { label: t('filters.types.company'), value: CUSTOMER_TYPE.COMPANY },
    ],
    [t],
  );

  return (
    <>
      <ActionBar>
        <SearchInput
          ariaLabel={t('filters.search.ariaLabel')}
          inputClassName="w-[200px] text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid"
          onChange={onSearchChange ?? ((): void => undefined)}
          placeholder={t('filters.search.placeholder')}
          value={searchValue}
        />
        <ActionBarDivider />
        <MultiSelect
          {...MULTI_SELECT_COMMON_PROPS}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={statusFilter}
          label={t('filters.status')}
          onValueChange={onStatusFilterChange ?? ((): void => undefined)}
          options={statusOptions}
          placeholder={t('filters.all')}
          searchable={false}
          variant="compact"
        />
        <MultiSelect
          {...MULTI_SELECT_COMMON_PROPS}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={typeFilter}
          label={t('filters.type')}
          onValueChange={onTypeFilterChange ?? ((): void => undefined)}
          options={typeOptions}
          placeholder={t('filters.all')}
          searchable={false}
          variant="compact"
        />
        <ScopeFilter
          onScopeChange={onScopeFilterChange ?? ((): void => undefined)}
          scopeValues={scopeFilter}
          siteId={siteId}
          siteOptions={siteOptions}
          spaceId={spaceId}
          spaceSiteGroups={spaceSiteGroups}
        />
        <ActionBarSpacer />
        <RequirePermission permission="customers:manage">
          <Button
            className="h-9 gap-1.5 rounded-md px-3 text-sm shadow-sm hover:shadow-md"
            onClick={onCreateClick}
            type="button"
            variant="default"
          >
            <Plus className="h-4 w-4" />
            {t('create.button')}
          </Button>
        </RequirePermission>
      </ActionBar>
    </>
  );
}

// ─── Private sub-components ───────────────────────────────────────────────────

interface ScopeFilterProps {
  onScopeChange: (values: string[]) => void;
  scopeValues: string[];
  siteId: string | null;
  siteOptions: MultiSelectOption[];
  spaceId: string | null;
  spaceSiteGroups: GroupedMultiSelectGroup[];
}

function ScopeFilter({
  onScopeChange,
  scopeValues,
  siteId,
  siteOptions,
  spaceId,
  spaceSiteGroups,
}: ScopeFilterProps): React.JSX.Element | null {
  const { t } = useTranslation('customers');

  if (siteId) {
    return null;
  }

  if (spaceId) {
    return (
      <MultiSelect
        {...MULTI_SELECT_COMMON_PROPS}
        className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
        defaultValue={scopeValues}
        label={t('filters.scope')}
        onValueChange={onScopeChange}
        options={siteOptions}
        placeholder={t('filters.all')}
        searchable={false}
        variant="compact"
      />
    );
  }

  return (
    <GroupedMultiSelect
      className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
      defaultValue={scopeValues}
      emptyText={t('filters.noResults')}
      groups={spaceSiteGroups}
      maxCount={2}
      onValueChange={onScopeChange}
      placeholder={t('filters.scope')}
      searchPlaceholder={t('filters.searchPlaceholder')}
      searchable
      selectAllText={t('filters.selectAll')}
      unselectAllText={t('filters.unselectAll')}
    />
  );
}
